import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./E3RadioGroup.module.css";
import { isEmpty } from "lodash";
import { getChildrenCommonCode } from "e3-common/utils/commonCodeUtil";
import useFetch from "e3-common/hooks/useFetch";

/**
 * E3RadioGroup
 * @type React.FC<E3RadioGroupPropTypes>
 */
const E3RadioGroup = React.forwardRef((props, ref) => {
  const { request } = useFetch();

  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3RadioGroupPropTypes).includes(p)) {
      console.error(`E3RadioGroup: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  const {
    disabled,
    readOnly,
    defaultValue,
    dataSource,
    parentCodeId,
    url,
    apiMethod,
    apiBody,
    labelId,
    valueId,
    onChange,
  } = props;

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 삭제(초기화) */
    clear() {
      setCurrState((prev) => ({
        ...prev,
        currValue: props.defaultValue ?? currState.currValue,
      }));
    },
    /** 값 가져오기 */
    getValue() {
      return currState.currValue;
    },
    /** 값 지정 */
    setValue(v) {
      setCurrState((prev) => ({
        ...prev,
        currValue: v !== null ? v : "",
      }));
    },
    enable() {
      setCurrState((prev) => ({ ...prev, disabled: false }));
    },
    disable() {
      setCurrState((prev) => ({ ...prev, disabled: true }));
    },
    setDataBinding(source) {
      _setDataBinding(source);
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error("E3RadioGroup: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다.");
      }
    },
  }));
  /**
   * E3RadioGroup Component의 State
   */
  const [currState, setCurrState] = useState({
    disabled: disabled,
    readOnly: readOnly,
    currValue: defaultValue,
    dataSource: dataSource || [],
  });

  const { currValue } = currState;

  useEffect(() => {
    if (!isEmpty(dataSource)) return _setDataBinding(dataSource);

    if (parentCodeId) {
      _getCommonCodesAndSet(parentCodeId);
      return;
    }

    if (url) _getDataFromAPIAndSet(url, apiMethod, apiBody);
  }, []);

  // RadioGroup 랜더링 후, 데이터 바인딩
  // 공통코드 데이터인 경우, LocalStorage에서 가져와 바인딩
  const _getCommonCodesAndSet = async (parentCodeId) => {
    const codes = await getChildrenCommonCode(parentCodeId);

    if (!isEmpty(codes)) _setDataBinding(codes, { isFormatOptions: true });
  };

  // url이 지정된 경우, API를 통해 데이터 가져와 바인딩
  const _getDataFromAPIAndSet = async (url, method, body) => {
    const response = await request(url, { method: method || "GET", body }, { showConfirm: false, showLoad: false });

    if (response) _setDataBinding(response, { isFormatOptions: true });
  };

  const _setDataBinding = (source, options) => {
    const { isFormatOptions } = options || {};

    const dataSource = isFormatOptions
      ? source?.map((item) => ({ label: item[labelId], value: item[valueId] }))
      : source;

    const matchedValue = (dataSource || []).find(({ value }) => value === currValue)?.value;

    setCurrState((prev) => ({ ...prev, dataSource, currValue: matchedValue }));
  };

  const _onChange = (e) => {
    setCurrState((prev) => {
      const updatedState = { ...prev, currValue: e.target.value };

      if (props.onChange) props.onChange(updatedState, e);

      return updatedState;
    });
  };

  return (
    <div className={`${styles.e3RadioGroup} ${props.direction} ${props.controlWidth ?? ""} ${props.className ?? ""}`}>
      {currState.dataSource?.map((item) => (
        <div key={item.value} className={styles.e3Radio}>
          {props.labelPosition === "left" ? <label htmlFor={item.value}>{item.label}</label> : null}
          <input
            type="radio"
            disabled={currState.disabled}
            readOnly={currState.readOnly}
            value={item.value}
            id={item.value}
            checked={item.value == currState.currValue}
            onChange={_onChange}
          />
          {props.labelPosition === "right" ? <label htmlFor={item.value}>{item.label}</label> : null}
        </div>
      ))}
      {props.comment ? (
        <div class="con_comment">
          <label>{props.comment}</label>
        </div>
      ) : null}
    </div>
  );
});

const E3RadioGroupPropTypes = {
  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  disabled: PropTypes.bool,
  /**
   * readOnly: 읽기전용
   * 기본값 false
   * updateState('readOnly', bool) 함수로 수정 가능
   */
  readOnly: PropTypes.bool,
  labelPosition: PropTypes.string,
  direction: PropTypes.string,
  dataSource: PropTypes.array,
  controlWidth: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentCodeId: PropTypes.string,
  valueId: PropTypes.string,
  labelId: PropTypes.string,
  url: PropTypes.string,
  apiMethod: PropTypes.string,
  apiBody: PropTypes.object,
  onChange: PropTypes.func,
};

E3RadioGroup.propTypes = E3RadioGroupPropTypes;
E3RadioGroup.defaultProps = {
  disabled: false,
  readOnly: false,
  labelPosition: "right",
  direction: "row",
  dataSource: [],
  defaultValue: null,
  parentCodeId: null,
  valueId: "codeId",
  labelId: "codeName",
  url: null,
  apiMethod: null,
  apiBody: null,
  onChange: null,
};
E3RadioGroup.displayName = "E3RadioGroup";
export default E3RadioGroup;
